import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { buildCityService, buildBreadcrumb } from '../../../../lib/structuredData';
import Header from '../../../../components/ui/Header';
import Footer from '../../../../components/Footer';
import { openCustomerPortal } from '../../../../utils/cn';

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_ACCESS_TOKEN;

const AddressInputWithMapbox = ({ placeholder, buttonText, buttonId, microcopy, darkMicrocopy }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchSuggestions = async (val) => {
    if (!val || val?.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(val)}.json?access_token=${MAPBOX_TOKEN}&types=address&country=US&proximity=-82.9988,39.9612&autoComplete=true`
      );
      const data = await res?.json();
      setSuggestions(data?.features || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const val = e?.target?.value;
    setQuery(val);
    clearTimeout(debounceRef?.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (place) => {
    setQuery(place?.place_name || '');
    setSuggestions([]);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSuggestions([]);
    // Carry the typed address into the portal so the wizard opens pre-filled.
    openCustomerPortal(query);
  };

  return (
    <div className="relative w-full">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={handleChange}
            placeholder={placeholder}
            className="w-full px-5 py-4 rounded-full text-gray-800 text-sm outline-none border border-gray-200 focus:border-blue-400 bg-white"
            style={{ fontFamily: "'Inter', sans-serif", boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
            autoComplete="off"
          />
          {suggestions?.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl overflow-hidden z-50"
              style={{ boxShadow: '0 8px 32px rgba(0,1,80,0.14)', border: '1px solid rgba(7,176,233,0.15)' }}
            >
              {suggestions?.map((s) => (
                <button
                  key={s?.id}
                  type="button"
                  onClick={() => handleSelect(s)}
                  className="w-full text-left px-5 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-150 border-b border-gray-50 last:border-0"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <span className="font-medium text-gray-900">{s?.text}</span>
                  <span className="text-gray-400 ml-1">{s?.place_name?.replace(s?.text, '')}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="px-8 py-4 rounded-full text-white font-bold text-sm whitespace-nowrap transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
          style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 20px rgba(7,176,233,0.35)', fontFamily: "'Poppins', 'Inter', sans-serif" }}
        >
          {loading ? 'Loading...' : buttonText}
        </button>
      </form>
      {microcopy && (
        <p className="text-center text-xs mt-3" style={{ color: darkMicrocopy ? '#6B7280' : 'rgba(255,255,255,0.65)', fontFamily: "'Inter', sans-serif" }}>
          {microcopy}
        </p>
      )}
      {/* Hidden Demand IQ trigger */}
      <button id="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea" className="hidden" aria-hidden="true" />
    </div>
  );
};

const ColumbusServiceAreaPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA', fontFamily: "'Poppins', 'Inter', sans-serif" }}>
      <Helmet>
        <title>Top Rated Columbus Roofing Companies | Exact Online Roof Prices</title>
        <meta
          name="description"
          content="Columbus, Ohio homeowners trust OpenbooQ for hassle-free roof replacements. Get a roof price and connect with certified local roofing professionals."
        />
        <meta name="keywords" content="columbus roofing companies, roofing companies columbus, columbus ohio roofing, roof replacement columbus, instant roof estimate columbus, central ohio roofing" />
        <script type="application/ld+json">
          {JSON.stringify(buildCityService({ name: 'Columbus', region: 'OH', url: 'https://openbooq.com/service-areas/ohio/columbus' }))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(buildBreadcrumb([
            { name: 'Home', item: 'https://openbooq.com' },
            { name: 'Service Areas', item: 'https://openbooq.com/service-areas' },
            { name: 'Columbus, OH', item: 'https://openbooq.com/service-areas/ohio/columbus' },
          ]))}
        </script>
      </Helmet>
      <Header />

      {/* ===== HERO SECTION ===== */}
      <section
        className="relative min-h-screen flex items-center"
        style={{ backgroundColor: '#000150', paddingTop: '80px' }}
      >
        {/* Background image overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/assets/images/102164693_w-002ccd95074448b2a07402913b6a6023-1773088071148.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {/* Dark navy tint overlay */}
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(0,1,80,0.55)' }}
        />
        {/* Subtle grid */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-grid-col" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#07B0E9" strokeWidth="0.8" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid-col)" />
          </svg>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Headline + Trust Badges */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                Columbus, Ohio
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
              >
                Roofing Reimagined
                <br />
                <span style={{ color: '#07B0E9' }}>for Columbus, Ohio.</span>
              </h1>

              <p className="text-gray-300 text-lg sm:text-xl mb-8 leading-relaxed max-w-xl">
                Same premium materials. Better warranty. Thousands less. Skip the pushy sales reps and get an accurate, AI-powered roof replacement price in minutes from your phone or laptop.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 mb-10">
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">Energy Star Certified</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">Impact Resistant</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">30-Minute Roof Price</span>
                </div>
              </div>
            </div>

            {/* Right: White Card with Address Input */}
            <div className="flex justify-center lg:justify-end">
              <div
                className="w-full max-w-md bg-white rounded-2xl p-8"
                style={{ boxShadow: '0 24px 64px rgba(0,1,80,0.25), 0 4px 16px rgba(7,176,233,0.12)', borderRadius: '20px' }}
              >
                <div className="text-center mb-6">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3"
                    style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    Free Roof Price
                  </div>
                  <h2
                    className="text-xl font-black mb-1"
                    style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}
                  >
                    Get My Roof Price
                  </h2>
                  <p className="text-gray-500 text-sm">Enter your Columbus address below</p>
                </div>

                <div className="space-y-3">
                  <AddressInputWithMapbox
                    placeholder="Enter your Columbus home address..."
                    buttonText="Get My Roof Price"
                    buttonId="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea"
                    microcopy={null}
                  />
                </div>

                <p
                  className="text-center text-xs mt-4"
                  style={{ color: '#6B7280', fontFamily: "'Inter', sans-serif" }}
                >
                  Free roof price • No sales calls • Serving all of Central Ohio
                </p>

                {/* Trust row */}
                <div className="flex items-center justify-center gap-4 mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" style={{ color: '#22C55E' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs text-gray-500">No credit card</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" style={{ color: '#22C55E' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs text-gray-500">No sales calls</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" style={{ color: '#22C55E' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs text-gray-500">30 minutes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BETTER WAY SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
              >
                A Better Way
              </div>
              <h2
                className="text-3xl sm:text-4xl font-black mb-6 leading-tight"
                style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
              >
                A Smarter Alternative to Traditional Columbus Roofing Companies
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                Getting a new roof shouldn't feel like buying a used car. The endless phone calls, taking time off work to meet contractors, and sitting through high-pressure sales presentations are a thing of the past. As the most innovative among columbus roofing companies, OpenbooQ lets you design, purchase, and schedule your entire roof replacement 100% online.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Using our proprietary <strong style={{ color: '#033494' }}>SmartPick™ AI technology</strong>, we measure your roof with pinpoint satellite accuracy. This cuts out the expensive overhead and massive sales commissions that traditional roofing companies columbus homeowners use have to charge. The result? We pass those savings directly to you, providing a premium roof from top brands like GAF, Atlas, TAMKO, and Owens Corning for thousands less.
              </p>

              <div className="flex flex-col gap-4">
                {[
                  { icon: '🛰️', title: 'Satellite Measurement', desc: 'Pinpoint-accurate roof measurements via satellite imagery—no site visit needed.' },
                  { icon: '💰', title: 'Thousands in Savings', desc: 'Lower overhead means we pass real savings directly to you.' },
                  { icon: '🏠', title: '100% Online Process', desc: 'Design, purchase, and schedule your roof from your couch.' },
                ]?.map((item) => (
                  <div key={item?.title} className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
                      style={{ backgroundColor: '#EBF8FE' }}
                    >
                      {item?.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm mb-0.5" style={{ color: '#000150' }}>{item?.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item?.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: SmartPick AI Mockup */}
            <div className="flex justify-center">
              <div
                className="relative w-full max-w-lg rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 24px 64px rgba(0,1,80,0.15)', borderRadius: '20px' }}
              >
                <div
                  className="rounded-2xl p-6"
                  style={{ background: 'linear-gradient(135deg, #000150 0%, #033494 100%)' }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <div className="flex-1 mx-4 h-6 rounded-full bg-white/10 flex items-center px-3">
                      <span className="text-white/50 text-xs">openbooq.com/smartpick</span>
                    </div>
                  </div>
                  <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#F8F9FA' }}>
                    <img
                      src="/assets/images/roof_top-1768329177363.png"
                      alt="SmartPick AI technology scanning a Columbus Ohio roof via satellite wireframes on a laptop screen"
                      className="w-full object-cover"
                      style={{ height: '220px' }}
                    />
                    <div className="p-4">
                      <div
                        className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: '#EBF8FE' }}
                      >
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#07B0E9' }} />
                        <span className="text-xs font-bold" style={{ color: '#07B0E9' }}>SmartPick™ AI — Scanning Roof...</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {['Roof Area', 'Pitch', 'Complexity']?.map((label, i) => (
                          <div key={label} className="bg-white rounded-lg p-2 text-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <div className="text-xs font-black" style={{ color: '#000150' }}>
                              {i === 0 ? '2,480 sq ft' : i === 1 ? '6/12' : 'Moderate'}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: '#07B0E9', color: '#fff', boxShadow: '0 4px 12px rgba(7,176,233,0.4)' }}
                >
                  SmartPick™ AI
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PREMIUM MATERIALS SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Premium Materials
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              Premium Shingles, Installed by Local Experts
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              From blazing summer heat to freezing winter ice storms, your roof needs to protect your home year-round. We install industry-leading, impact-resistant shingles designed for Ohio's demanding weather.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              {
                name: 'Titan XT',
                brand: 'TAMKO',
                tagline: 'Proven Durability',
                desc: "TAMKO's best-selling architectural shingle. Exceptional dimensional look and long-lasting performance perfect for Central Ohio homes.",
                swatches: ['#4A4A4A', '#8B7355', '#9E9E9E', '#6B5B45', '#3D3D3D'],
                image: '/assets/images/tamko-titan-xt-rustic-hickory-house_1_-1768340280949.jpg',
                imageAlt: 'House with TAMKO Titan XT shingles installed in Columbus Ohio',
              },
              {
                name: 'Timberline HDZ',
                brand: 'GAF',
                tagline: 'Most Popular in America',
                desc: "America's #1-selling shingle. LayerLock technology and StainGuard Plus protection. Ideal for Columbus's demanding climate with superior wind resistance.",
                swatches: ['#2C2C2C', '#8B7355', '#A0A0A0', '#7A6A5A', '#8A7A6A'],
                image: '/assets/images/gaf-timberline-hdz-charcoal-house_1_-1768332752421.jpg',
                imageAlt: 'House with GAF Timberline HDZ shingles in charcoal installed in Columbus Ohio',
                featured: true,
              },
              {
                name: 'Duration',
                brand: 'Owens Corning',
                tagline: 'SureNail Technology',
                desc: "Owens Corning's flagship shingle with patented SureNail Technology for exceptional wind resistance—perfect for Dublin, Westerville, and New Albany homes.",
                swatches: ['#1A1A1A', '#8B7355', '#8C8C8C', '#4A4A5A', '#C4A882'],
                image: '/assets/images/owens-corning-oakridge-shingles-onyx-black-house_1_-1768334487021.jpg',
                imageAlt: 'House with Owens Corning Duration shingles installed in Columbus Ohio',
              },
            ]?.map((shingle) => (
              <div
                key={shingle?.name}
                className="bg-white rounded-2xl overflow-hidden flex flex-col relative"
                style={{
                  borderRadius: '12px',
                  boxShadow: shingle?.featured
                    ? '0 20px 48px rgba(7,176,233,0.15), 0 4px 16px rgba(0,1,80,0.08)'
                    : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                  border: shingle?.featured ? '2px solid rgba(7,176,233,0.3)' : '1px solid rgba(0,0,0,0.04)',
                }}
              >
                {shingle?.featured && (
                  <div
                    className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold"
                    style={{ backgroundColor: '#07B0E9', color: '#fff' }}
                  >
                    Most Popular
                  </div>
                )}
                <div className="relative overflow-hidden" style={{ height: '200px' }}>
                  <img
                    src={shingle?.image}
                    alt={shingle?.imageAlt}
                    className="w-full h-full object-cover"
                  />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-16"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)' }}
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="mb-3">
                    <span
                      className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                      style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
                    >
                      {shingle?.brand}
                    </span>
                  </div>
                  <h3
                    className="text-xl font-black mb-1"
                    style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}
                  >
                    {shingle?.name}
                  </h3>
                  <p className="text-xs font-semibold mb-3" style={{ color: '#07B0E9' }}>{shingle?.tagline}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{shingle?.desc}</p>

                  {/* Color swatches */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs text-gray-400 font-medium">Colors:</span>
                    <div className="flex gap-1.5">
                      {shingle?.swatches?.map((color, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 rounded-full border-2 border-white cursor-pointer hover:scale-110 transition-transform"
                          style={{ backgroundColor: color, boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Feature bullets */}
                  <div className="flex flex-col gap-1.5 mb-5">
                    {['Limited Lifetime Warranty', 'Energy Star Certified', 'Impact Resistant']?.map((feat) => (
                      <div key={feat} className="flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-xs text-gray-600 font-medium">{feat}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/shingles"
                    className="block text-center py-3 px-6 rounded-full text-white font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
                    style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 16px rgba(7,176,233,0.3)' }}
                  >
                    Select Product
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COLUMBUS WEATHER / TRUST SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-5xl mx-auto">
          <div
            className="rounded-3xl p-10 sm:p-14 text-white text-center relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)',
              boxShadow: '0 24px 64px rgba(3,52,148,0.25)',
            }}
          >
            {/* Background pattern */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
              <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="weather-grid-col" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="1" fill="white" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#weather-grid-col)" />
              </svg>
            </div>

            <div className="relative z-10">
              {/* Weather Shield Icon + Season Icons */}
              <div className="flex items-center justify-center mb-8">
                {/* Central Shield */}
                <div className="flex flex-col items-center">
                  <svg className="w-16 h-16 mb-2" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">All-Season Shield</span>
                </div>
              </div>

              {/* Season icons row */}
              <div className="flex items-center justify-center gap-6 sm:gap-10 mb-8">
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-9 h-9 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">Summer Heat</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-9 h-9 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19l-1 2m4-2l-1 2m4-2l-1 2" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">Hail Storms</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-9 h-9 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18M3 12h18M5.636 5.636l12.728 12.728M18.364 5.636L5.636 18.364" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">Snow & Ice</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-9 h-9 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.59 4.59A2 2 0 004 12H2m10.59 11.41A2.5 2.5 0 0119.5 12H2" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">High Winds</span>
                </div>
              </div>

              <h2
                className="text-3xl sm:text-4xl font-black mb-5 leading-tight"
                style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
              >
                Built for Central Ohio's
                <br />
                <span className="opacity-90">Distinct Four Seasons.</span>
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
                Columbus weather demands a roof that can handle it all—blazing summer heat, powerful spring hailstorms, and brutal winter ice dams. Our premium shingles from GAF, Atlas, TAMKO, and Owens Corning are engineered specifically for Ohio's extreme weather cycles, with Class 4 impact resistance and wind ratings up to 130 mph.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {[
                  { stat: '130 mph', label: 'Wind Resistance Rating' },
                  { stat: 'Class 4', label: 'Impact Resistance' },
                  { stat: 'Lifetime', label: 'Limited Warranty' },
                ]?.map((item) => (
                  <div
                    key={item?.label}
                    className="rounded-xl p-4"
                    style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}
                  >
                    <div className="text-2xl font-black mb-1">{item?.stat}</div>
                    <div className="text-xs opacity-75 font-medium uppercase tracking-wider">{item?.label}</div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  openCustomerPortal();
                }}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-sm transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{ backgroundColor: '#fff', color: '#033494', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
              >
                Get My Storm-Ready Roof Price
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINANCING SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Flexible Payment Options
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              Transparent Pricing. No Surprises.
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              Traditional estimates often change once the work starts. With OpenbooQ, the price you see is the price you pay. Choose to pay 100% upfront for the lowest price, use our secure Escrow protection (funds are only released when you are 100% satisfied), or apply for flexible monthly financing for as low as $491/mo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: '💵',
                title: 'Pay Upfront',
                subtitle: 'Lowest Price Guaranteed',
                desc: 'Pay 100% upfront and lock in our absolute best price. No financing fees, no interest—just the most transparent roofing deal in Columbus.',
                highlight: 'Best Price',
                highlightColor: '#22C55E',
                cta: 'Get Upfront Price',
              },
              {
                icon: '🔒',
                title: 'Escrow Protection',
                subtitle: 'Secure & Protected',
                desc: 'Your funds are held securely in escrow and only released upon your 100% satisfaction. Complete peace of mind from day one to final inspection.',
                highlight: 'Most Secure',
                highlightColor: '#07B0E9',
                cta: 'Learn About Escrow',
                featured: true,
              },
              {
                icon: '📅',
                title: 'Monthly Financing',
                subtitle: 'From $491/month',
                desc: 'Spread the cost of your new roof over time with competitive financing rates. Get the roof you need today without breaking the bank.',
                highlight: 'Most Flexible',
                highlightColor: '#F59E0B',
                cta: 'See Financing Options',
              },
            ]?.map((option) => (
              <div
                key={option?.title}
                className="bg-white rounded-2xl p-8 flex flex-col"
                style={{
                  borderRadius: '12px',
                  boxShadow: option?.featured
                    ? '0 20px 48px rgba(7,176,233,0.15), 0 4px 16px rgba(0,1,80,0.08)'
                    : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                  border: option?.featured ? '2px solid rgba(7,176,233,0.3)' : '1px solid rgba(0,0,0,0.04)',
                }}
              >
                <div className="text-3xl mb-4">{option?.icon}</div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-black" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>
                    {option?.title}
                  </h3>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: option?.highlightColor + '20', color: option?.highlightColor }}
                  >
                    {option?.highlight}
                  </span>
                </div>
                <p className="text-xs font-semibold mb-3" style={{ color: '#07B0E9' }}>{option?.subtitle}</p>
                <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-6">{option?.desc}</p>
                <Link
                  to="/financing-options"
                  className="block text-center py-3 px-6 rounded-full font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                  style={{
                    backgroundColor: option?.featured ? '#07B0E9' : 'transparent',
                    color: option?.featured ? '#fff' : '#033494',
                    border: option?.featured ? 'none' : '2px solid #033494',
                  }}
                >
                  {option?.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA SECTION ===== */}
      <section
        className="py-24 px-4"
        style={{ backgroundColor: '#000150' }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
            style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            Start Your Roof Price
          </div>
          <h2
            className="text-3xl sm:text-4xl font-black text-white mb-5 leading-tight"
            style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
          >
            Ready to Get Your Exact Roof Price?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Join thousands of Columbus homeowners who've already discovered a smarter way to replace their roof. No sales calls. No pressure. Just your exact price in minutes.
          </p>

          <div className="max-w-xl mx-auto">
            <AddressInputWithMapbox
              placeholder="Enter your Columbus home address..."
              buttonText="Get My Roof Price"
              buttonId="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea-bottom-col"
              microcopy="Free roof price • No sales calls • Serving all of Central Ohio"
            />
            <button id="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea-bottom-col" className="hidden" aria-hidden="true" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
            {[
              { icon: '⚡', text: 'Roof price in 30 minutes' },
              { icon: '🚫', text: 'No sales calls ever' },
              { icon: '💯', text: 'Transparent pricing' },
              { icon: '🏆', text: 'Top-rated materials' },
            ]?.map((item) => (
              <div key={item?.text} className="flex items-center gap-2">
                <span className="text-base">{item?.icon}</span>
                <span className="text-gray-400 text-sm font-medium">{item?.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ColumbusServiceAreaPage;
